import { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

/** Editor for a scheduled curriculum lesson within The Nest — subject/title
 *  aren't editable here (that's the Planner's job), only when it happens
 *  today and whether it's still scheduled at all. Both actions write
 *  straight to the same planner_slots row the Planner page reads, so
 *  changes show up there too without any extra sync step. */
export default function LessonPopover({ trigger, lesson, open: controlledOpen, onOpenChange, onSave, onRemove }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;
  const [startTime, setStartTime] = useState(lesson?.start_time || '09:00');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(startTime);
      setOpen(false);
    } catch {
      // onSave already surfaces a toast; keep the popover open so the user can retry.
    }
    setSaving(false);
  };

  const handleRemove = async () => {
    try {
      await onRemove();
      setOpen(false);
    } catch {
      // onRemove already surfaces a toast
    }
  };

  if (!lesson) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger || <span />}</PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" data-testid="lesson-popover">
        <div>
          <p className="font-semibold text-sm text-fern-dark">{lesson.title}</p>
          <p className="text-xs text-slate-400">{lesson.duration_minutes} min lesson</p>
        </div>
        <div>
          <Label htmlFor="lesson-time">Time</Label>
          <Input id="lesson-time" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} data-testid="lesson-time-input" />
        </div>
        <div className="flex justify-between pt-1">
          <Button variant="ghost" className="text-slate-500 hover:text-slate-800" onClick={handleRemove} data-testid="lesson-remove-btn">
            Remove from this day
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-fern hover:bg-fern-dark text-white" data-testid="lesson-save-btn">
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
